/* assets/store-locator.js — JuJu's Protein Snacks
 *
 * Reads the retailer list from window.JUJU_STORE_LOCATIONS, defined in
 * assets/store-locations.js. No metaobjects, no Storefront API, no access
 * token, and no 250-entry cap — the data ships inside the theme, so it
 * survives a theme export/import instead of being left behind in the old
 * store.
 *
 * Search matches the store list directly, and only falls back to the
 * Nominatim geocoder for queries that are genuinely places. Sending every
 * query to the geocoder is what made "Lift The City" fly the map to
 * "Lift Station No. 3, Yellowknife".
 */
(function () {
  'use strict';

  var NOMINATIM = 'https://nominatim.openstreetmap.org/search';
  var TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
  var TILE_ATTRIBUTION =
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

  // Nominatim result categories we accept as a real "place" to centre on.
  // "Lift The City" comes back as category:landuse / type:industrial and is
  // rejected here. A postal code comes back as category:place / type:postcode
  // with a LOW importance score, which is why we filter on category and not
  // on importance.
  var PLACE_CATEGORIES = { place: 1, boundary: 1 };
  var PLACE_ADDRESSTYPES = {
    city: 1, town: 1, village: 1, hamlet: 1, suburb: 1, neighbourhood: 1,
    quarter: 1, borough: 1, municipality: 1, county: 1, region: 1,
    state: 1, province: 1, postcode: 1, island: 1, locality: 1
  };

  var CA_POSTAL = /^[a-z]\d[a-z][ -]?(\d[a-z]\d)?$/i;
  var US_ZIP = /^\d{5}(-\d{4})?$/;

  /* ---------------------------------------------------------------- utils */

  // Lowercase, strip accents, collapse whitespace and punctuation.
  // "Popeye's Suppléments Boucherville" -> "popeye s supplements boucherville"
  // Apostrophes are DELETED rather than turned into a space, so "Popeye's"
  // becomes "popeyes" and matches what a shopper actually types. Everything
  // else non-alphanumeric becomes a space.
  function normalize(str) {
    var s = String(str == null ? '' : str);
    if (typeof s.normalize === 'function') {
      s = s.normalize('NFD').replace(/[̀-ͯ]/g, '');
    }
    return s
      .toLowerCase()
      .replace(/['‘’ʼ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function escapeRe(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Phrase present, anchored to the start of a word.
  // "super c" does NOT match "metro supermarche claka" — the run after the
  // word boundary is "supermarche", which does not begin "super c".
  function hasPhrase(hay, phrase) {
    if (!phrase) return false;
    return new RegExp('(^| )' + escapeRe(phrase)).test(hay);
  }

  // Whole word, e.g. "super" matches "super" but never "supermarche".
  function hasWord(hay, word) {
    return new RegExp('(^| )' + escapeRe(word) + '( |$)').test(hay);
  }

  // Word starting with this fragment — used for the last token only, so
  // typing "sher" still finds Sherbrooke.
  function hasWordPrefix(hay, frag) {
    return new RegExp('(^| )' + escapeRe(frag)).test(hay);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function formatDistance(km, useMiles) {
    return useMiles ? (km * 0.621371).toFixed(1) + ' mi' : km.toFixed(1) + ' km';
  }

  function looksLikePostal(q) {
    var bare = q.replace(/\s+/g, '');
    return CA_POSTAL.test(bare) || US_ZIP.test(bare);
  }

  /* ----------------------------------------------------------------- init */

  function init(section) {
    var mapEl = section.querySelector('[data-locator-map]');
    var listEl = section.querySelector('[data-locator-list]');
    var statusEl = section.querySelector('[data-locator-status]');
    var form = section.querySelector('[data-locator-search]');
    var input = section.querySelector('[data-locator-input]');
    var geoBtn = section.querySelector('[data-locator-geolocate]');

    if (!mapEl || typeof L === 'undefined') return;

    // The store list lives in assets/store-locations.js, loaded just before
    // this file. Both are `defer`, and defer preserves document order, so the
    // global is already populated by the time this runs.
    var locations = Array.isArray(window.JUJU_STORE_LOCATIONS)
      ? window.JUJU_STORE_LOCATIONS.slice()
      : [];
    var radiusKm = parseFloat(section.dataset.searchRadius) || 100;
    var noResultsText = section.dataset.noResultsText || 'No retailers found nearby.';
    var markers = [];
    var activeIndex = -1;

    var map = L.map(mapEl, { scrollWheelZoom: false }).setView(
      [parseFloat(section.dataset.defaultLat), parseFloat(section.dataset.defaultLng)],
      parseInt(section.dataset.defaultZoom, 10) || 5
    );
    L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 19 }).addTo(map);

    var cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 45,
      iconCreateFunction: function (c) {
        return L.divIcon({
          html: '<span>' + c.getChildCount() + '</span>',
          className: 'store-locator__cluster',
          iconSize: [40, 40]
        });
      }
    });
    map.addLayer(cluster);

    /* -------------------------------------------------------- search index */

    // Built once per location and cached on the object.
    function squash(s) {
      return s.replace(/ /g, '');
    }

    function haystack(loc) {
      if (loc._hay) return loc._hay;
      var h = {
        name: normalize(loc.name),
        city: normalize(loc.city),
        province: normalize(loc.province),
        address: normalize(loc.address),
        postal: normalize(loc.postalCode),
        all: normalize(
          [loc.name, loc.address, loc.city, loc.province, loc.postalCode]
            .filter(Boolean)
            .join(' ')
        )
      };
      // Space-free copies. "Popeye's" normalizes to "popeye s", so a shopper
      // typing "popeyes" would otherwise match nothing. Same for a postal
      // code typed without the space ("m5v4a6").
      h.nameSquash = squash(h.name);
      h.citySquash = squash(h.city);
      h.postalSquash = squash(h.postal);
      h.allSquash = squash(h.all);
      return (loc._hay = h);
    }

    // Every token must appear somewhere. Score rewards name/city/postal hits
    // so "Sherbrooke" ranks the Sherbrooke stores above an address that
    // merely contains the word.
    /**
     * Two tiers, strongest first. A tier only runs if the one above it found
     * nothing, so a loose match can never outrank or dilute a precise one.
     *
     *   Tier 1  the query appears as a phrase starting at a word boundary
     *   Tier 2  every token matches a whole word (last token may be a prefix)
     *
     * There is deliberately NO loose substring tier. "super c" used to return
     * four Metro Supermarché stores, because "super" sits inside
     * "supermarche" and "c" sits inside almost everything. Returning nothing
     * is the correct answer when no store is actually called that.
     */
    function scoreLocation(loc, tokens, q, qs, tier) {
      var h = haystack(loc);
      var score = 0;

      if (tier === 1) {
        var phrase = hasPhrase(h.all, q) ||
                     (qs.length >= 4 && h.allSquash.indexOf(qs) > -1);
        if (!phrase) return 0;
      } else {
        for (var i = 0; i < tokens.length; i++) {
          var last = i === tokens.length - 1;
          var ok = last ? hasWordPrefix(h.all, tokens[i]) : hasWord(h.all, tokens[i]);
          if (!ok && h.allSquash.indexOf(tokens[i]) > -1 && tokens[i].length >= 6) {
            ok = true; // squashed names like "protein911"
          }
          if (!ok) return 0;
        }
      }

      // Rank: name beats city beats postal beats address.
      if (h.name === q) score += 300;
      else if (hasPhrase(h.name, q)) score += 200;
      else if (h.nameSquash.indexOf(qs) > -1) score += 150;

      // A name that BEGINS with the query beats one that merely contains it,
      // so "metro" ranks "Metro - Marché X" above
      // "Espace Shop Santé - Métro Beaulieu".
      if (h.name.indexOf(q) === 0 || h.nameSquash.indexOf(qs) === 0) score += 60;

      if (h.city === q) score += 120;
      else if (hasPhrase(h.city, q)) score += 90;

      if (qs.length >= 3 && h.postalSquash.indexOf(qs) === 0) score += 140;
      if (h.province === q) score += 60;
      if (hasPhrase(h.address, q)) score += 50;

      for (var j = 0; j < tokens.length; j++) {
        if (hasWordPrefix(h.name, tokens[j])) score += 15;
        if (hasWordPrefix(h.city, tokens[j])) score += 10;
      }

      return score || 1;
    }

    function searchLocal(rawQuery) {
      var q = normalize(rawQuery);
      var qs = squash(q);
      var tokens = q.split(' ').filter(Boolean);
      if (!tokens.length) return [];

      for (var tier = 1; tier <= 2; tier++) {
        var hits = [];
        for (var i = 0; i < locations.length; i++) {
          var s = scoreLocation(locations[i], tokens, q, qs, tier);
          if (s > 0) hits.push({ loc: locations[i], index: i, score: s, dist: null });
        }
        if (hits.length) {
          hits.sort(function (a, b) {
            return b.score - a.score || a.loc.name.localeCompare(b.loc.name);
          });
          return hits;
        }
      }
      return [];
    }

    /* ---------------------------------------------------------- map pieces */

    function pinIcon(active) {
      return L.divIcon({
        className: 'store-locator__pin-wrap',
        html: '<div class="store-locator__pin' + (active ? ' is-active' : '') + '"></div>',
        iconSize: [30, 40],
        iconAnchor: [15, 40],
        popupAnchor: [0, -38]
      });
    }

    function directionsUrl(loc) {
      var dest = [
        loc.name,
        loc.address,
        loc.city,
        [loc.province, loc.postalCode].filter(Boolean).join(' ')
      ].filter(Boolean).join(', ');
      return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(dest);
    }

    function popupHtml(loc) {
      var html =
        '<strong>' + escapeHtml(loc.name) + '</strong><br>' +
        escapeHtml(loc.address) + '<br>' +
        escapeHtml(loc.city) + ', ' + escapeHtml(loc.province) + ' ' +
        escapeHtml(loc.postalCode || '');
      html +=
        '<br><a href="' + directionsUrl(loc) + '" target="_blank" rel="noopener">Get directions</a>';
      return html;
    }

    function buildMarkers() {
      cluster.clearLayers();
      markers = locations.map(function (loc, i) {
        var m = L.marker([loc.lat, loc.lng], { icon: pinIcon(false) });
        m.bindPopup(popupHtml(loc));
        m.on('click', function () {
          setActive(i, { scrollList: true, openPopup: false });
        });
        cluster.addLayer(m);
        return m;
      });
    }

    function setActive(index, opts) {
      opts = opts || {};
      if (activeIndex > -1 && markers[activeIndex]) {
        markers[activeIndex].setIcon(pinIcon(false));
      }
      activeIndex = index;
      var loc = locations[index];
      if (!loc) return;

      if (markers[index]) {
        markers[index].setIcon(pinIcon(true));
        if (opts.openPopup !== false) {
          var targetZoom = Math.max(map.getZoom(), 16);
          map.flyTo([loc.lat, loc.lng], targetZoom, { duration: 0.9 });
          map.once('moveend', function () {
            cluster.zoomToShowLayer(markers[index], function () {
              markers[index].openPopup();
            });
          });
        }
      }

      listEl.querySelectorAll('[data-locator-card]').forEach(function (card) {
        var isActive = parseInt(card.dataset.index, 10) === index;
        card.classList.toggle('is-active', isActive);
        if (isActive && opts.scrollList) {
          card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      });
    }

    function distanceKm(aLat, aLng, bLat, bLng) {
      var R = 6371;
      var dLat = ((bLat - aLat) * Math.PI) / 180;
      var dLng = ((bLng - aLng) * Math.PI) / 180;
      var s =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((aLat * Math.PI) / 180) *
          Math.cos((bLat * Math.PI) / 180) *
          Math.sin(dLng / 2) *
          Math.sin(dLng / 2);
      return 2 * R * Math.asin(Math.sqrt(s));
    }

    /* ------------------------------------------------------------ the list */

    function cardHtml(loc, distLabel) {
      var distHtml = distLabel
        ? '<span class="store-locator__card-distance">' + distLabel + '</span>'
        : '';
      return (
        '<h3 class="store-locator__card-name">' + escapeHtml(loc.name) + distHtml + '</h3>' +
        '<p class="store-locator__card-address">' +
          escapeHtml(loc.address) + '<br>' +
          escapeHtml(loc.city) + ', ' + escapeHtml(loc.province) + ' ' +
          escapeHtml(loc.postalCode || '') +
        '</p>' +
        (loc.phone
          ? '<a class="store-locator__card-link" href="tel:' + escapeHtml(loc.phone) + '">' +
            escapeHtml(loc.phone) + '</a>'
          : '') +
        '<div class="store-locator__card-actions">' +
          '<a class="store-locator__card-link" href="' + directionsUrl(loc) +
            '" target="_blank" rel="noopener">Get directions</a>' +
          (loc.website
            ? '<a class="store-locator__card-link" href="' + escapeHtml(loc.website) +
              '" target="_blank" rel="noopener">Website</a>'
            : '') +
        '</div>'
      );
    }

    /**
     * items  - array of {loc, index, dist} or null to render everything
     * origin - {lat,lng} to sort/label by distance, or null
     */
    function renderList(origin, useMiles, items) {
      var ordered = items;

      if (!ordered) {
        ordered = locations.map(function (loc, i) {
          return {
            loc: loc,
            index: i,
            dist: origin ? distanceKm(origin.lat, origin.lng, loc.lat, loc.lng) : null
          };
        });
        if (origin) ordered.sort(function (a, b) { return a.dist - b.dist; });
      }

      listEl.innerHTML = '';

      if (!ordered.length) {
        var empty = document.createElement('li');
        empty.className = 'store-locator__empty';
        empty.textContent = noResultsText;
        listEl.appendChild(empty);
        return;
      }

      ordered.forEach(function (item) {
        var li = document.createElement('li');
        li.className = 'store-locator__card';
        li.setAttribute('data-locator-card', '');
        li.dataset.index = item.index;
        li.innerHTML = cardHtml(
          item.loc,
          item.dist != null ? formatDistance(item.dist, useMiles) : ''
        );
        li.addEventListener('click', function (e) {
          if (!e.target.closest('a')) setActive(item.index, { scrollList: false });
        });
        listEl.appendChild(li);
      });
    }

    function setStatus(msg, isError) {
      if (!msg) {
        statusEl.hidden = true;
        return;
      }
      statusEl.hidden = false;
      statusEl.textContent = msg;
      statusEl.classList.toggle('is-error', !!isError);
    }

    function fitTo(items) {
      var pts = items.map(function (it) { return [it.loc.lat, it.loc.lng]; });
      if (!pts.length) return;
      if (pts.length === 1) map.setView(pts[0], 14);
      else map.fitBounds(L.latLngBounds(pts).pad(0.2));
    }

    /* ------------------------------------------------- origin-based search */

    function applyOrigin(origin, useMiles) {
      var withDist = locations.map(function (loc, i) {
        return { loc: loc, index: i, dist: distanceKm(origin.lat, origin.lng, loc.lat, loc.lng) };
      }).sort(function (a, b) { return a.dist - b.dist; });

      var inRadius = withDist.filter(function (it) { return it.dist <= radiusKm; });

      if (!inRadius.length) {
        // Previously this showed the no-results message but left all 127
        // stores rendered underneath it. Show the nearest few instead, and
        // say plainly that they are outside the radius.
        var nearest = withDist.slice(0, 5);
        renderList(origin, useMiles, nearest);
        setStatus(
          noResultsText + ' Closest is ' + formatDistance(withDist[0].dist, useMiles) + ' away.',
          false
        );
        map.setView([origin.lat, origin.lng], 8);
        return;
      }

      renderList(origin, useMiles, inRadius);
      setStatus(
        'Showing ' + inRadius.length + ' retailer' + (inRadius.length === 1 ? '' : 's') +
        ' within ' + formatDistance(radiusKm, useMiles) + ' — closest is ' +
        formatDistance(inRadius[0].dist, useMiles) + ' away.'
      );
      var bounds = L.latLngBounds([[origin.lat, origin.lng]]);
      inRadius.forEach(function (it) { bounds.extend([it.loc.lat, it.loc.lng]); });
      map.fitBounds(bounds.pad(0.2));
    }

    function geocode(q) {
      var params = new URLSearchParams({
        q: q,
        format: 'jsonv2',
        limit: '5',
        countrycodes: 'ca,us',
        addressdetails: '1'
      });
      return fetch(NOMINATIM + '?' + params.toString(), {
        headers: { Accept: 'application/json' }
      })
        .then(function (r) { return r.json(); })
        .then(function (results) {
          if (!Array.isArray(results)) return null;
          // Keep only results that are genuinely a place.
          for (var i = 0; i < results.length; i++) {
            var r = results[i];
            var okCat = PLACE_CATEGORIES[r.category];
            var okType = PLACE_ADDRESSTYPES[r.addresstype] || PLACE_ADDRESSTYPES[r.type];
            if (okCat || okType) return r;
          }
          return null;
        });
    }

    /* ---------------------------------------------------------- form wiring */

    function runSearch(rawQuery) {
      var q = rawQuery.trim();
      if (!q) {
        resetView();
        return;
      }

      // 1. Match the store list first. A store name is not a place.
      var hits = searchLocal(q);
      if (hits.length) {
        renderList(null, false, hits);
        fitTo(hits);
        if (hits.length === 1) setActive(hits[0].index, { scrollList: false });
        setStatus(
          'Showing ' + hits.length + ' retailer' + (hits.length === 1 ? '' : 's') +
          ' matching “' + q + '”.'
        );
        return;
      }

      // 2. No local match — treat it as a place and geocode.
      setStatus('Searching…');
      geocode(q)
        .then(function (place) {
          if (!place) {
            renderList(null, false, []);
            setStatus(
              'No retailer or place matched “' + q + '” — try a city name or postal / zip code.',
              true
            );
            map.setView(
              [parseFloat(section.dataset.defaultLat), parseFloat(section.dataset.defaultLng)],
              parseInt(section.dataset.defaultZoom, 10) || 5
            );
            return;
          }
          var useMiles = !!(place.address && place.address.country_code === 'us');
          applyOrigin({ lat: parseFloat(place.lat), lng: parseFloat(place.lon) }, useMiles);
        })
        .catch(function () {
          setStatus('Location search is unavailable right now — browse the map or list below.', true);
        });
    }

    // Clearing the box restores exactly what the shopper saw on first load.
    function resetView() {
      renderList(null, false, null);
      setStatus('');
      map.setView(
        [parseFloat(section.dataset.defaultLat), parseFloat(section.dataset.defaultLng)],
        parseInt(section.dataset.defaultZoom, 10) || 5
      );
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      runSearch(input.value);
    });

    // Native "×" on <input type="search"> fires a search event; also catch
    // the user clearing the box by hand.
    input.addEventListener('search', function () {
      if (!input.value.trim()) resetView();
    });
    input.addEventListener('input', function () {
      if (!input.value.trim()) resetView();
    });

    geoBtn.addEventListener('click', function () {
      if (!navigator.geolocation) {
        setStatus('Location access is not supported by your browser.', true);
        return;
      }
      setStatus('Finding your location…');
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          var origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          var nearest = locations.reduce(function (best, loc) {
            var d = distanceKm(origin.lat, origin.lng, loc.lat, loc.lng);
            return d < best.d ? { d: d, loc: loc } : best;
          }, { d: Infinity, loc: null });
          var useMiles = nearest.loc ? nearest.loc.country === 'US' : false;
          applyOrigin(origin, useMiles);
        },
        function () {
          setStatus('Location access was denied — search by city or postal / zip code instead.', true);
        }
      );
    });

    /* ------------------------------------------------------- extra pages */

    /* --------------------------------------------------------- load stores
     *
     * The previous version read payload.pages and fetched
     *   ?section_id=<id>&page=2
     * for each extra page. On this store that request returns PAGE 1 AGAIN —
     * {% paginate %} over shop.metaobjects[...].values does not respond to the
     * ?page parameter, verified against both the section URL and the plain
     * page URL. So every store got concatenated onto itself and the locator
     * rendered 500 cards for 250 stores, while entries 251+ never loaded at
     * all. The fetch is removed: it could only ever duplicate.
     *
     * A dedupe pass still runs, because duplicate metaobject entries created
     * by a bulk import would otherwise show up as repeated pins.
     */
    function dedupe(list) {
      var seen = {};
      var out = [];
      for (var i = 0; i < list.length; i++) {
        var l = list[i];
        var k = [
          normalize(l.name), normalize(l.address),
          normalize(l.postalCode), l.lat, l.lng
        ].join('|');
        if (seen[k]) continue;
        seen[k] = true;
        out.push(l);
      }
      return out;
    }

    /* ------------------------------------------------------------ load data
     *
     * Nothing to fetch. The only work here is validating what the data file
     * gave us and collapsing accidental duplicates, so a copy-paste slip in
     * store-locations.js shows up as a console warning rather than a
     * duplicated pin or a store silently missing from the map.
     */
    function dedupe(list) {
      var seen = {};
      var out = [];
      for (var i = 0; i < list.length; i++) {
        var l = list[i];
        var k = [
          normalize(l.name), normalize(l.address),
          normalize(l.postalCode), l.lat, l.lng
        ].join('|');
        if (seen[k]) continue;
        seen[k] = true;
        out.push(l);
      }
      return out;
    }

    function usable(l) {
      return l && l.name &&
        isFinite(parseFloat(l.lat)) && isFinite(parseFloat(l.lng)) &&
        Math.abs(l.lat) <= 90 && Math.abs(l.lng) <= 180;
    }

    function loadLocations() {
      var supplied = locations.length;

      if (!supplied) {
        console.warn(
          '[store-locator] window.JUJU_STORE_LOCATIONS is empty or missing. ' +
          'Check that assets/store-locations.js exists and is loaded before ' +
          'this file in sections/store-locator.liquid.'
        );
        renderList(null, false, []);
        setStatus(noResultsText, true);
        return;
      }

      var dropped = locations.filter(function (l) { return !usable(l); });
      if (dropped.length) {
        console.warn(
          '[store-locator] Skipped ' + dropped.length + ' store(s) with a ' +
          'missing name or invalid coordinates: ' +
          dropped.slice(0, 5).map(function (l) {
            return (l && l.name) || '(unnamed)';
          }).join(', ') + (dropped.length > 5 ? ', …' : '')
        );
      }
      locations = locations.filter(usable);

      var beforeDedupe = locations.length;
      locations = dedupe(locations);
      if (beforeDedupe !== locations.length) {
        console.warn(
          '[store-locator] Collapsed ' + (beforeDedupe - locations.length) +
          ' duplicate store(s) in store-locations.js.'
        );
      }

      // Alphabetical is the most useful order before anyone searches.
      locations.sort(function (a, b) {
        return String(a.name).localeCompare(String(b.name), 'en', { sensitivity: 'base' });
      });

      buildMarkers();
      renderList(null, false, null);

      if (section.dataset.designMode === 'true') {
        setStatus(
          locations.length + ' retailers loaded from assets/store-locations.js.' +
          (dropped.length ? ' ' + dropped.length + ' skipped — see the browser console.' : '')
        );
      }
    }

    loadLocations();
  }

  function boot() {
    document.querySelectorAll('.store-locator[data-section-id]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();